import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from '@hqo/toast';

/** How long the CTA holds its confirmed ("Checked in") state before it starts leaving. */
export const CHECK_IN_CONFIRM_MS = 1600;

/** The fade that carries the CTA out once the hold is over. */
export const CHECK_IN_FADE_MS = 300;

/** How long a failure stays on screen. `autoClose` is the toast's own window, so nothing here times it. */
export const CHECK_IN_ERROR_MS = 5000;

/** One toast per tile, so a repeat failure on the same tile REPLACES its notice instead of stacking. */
export const checkInToastId = (id: string): string => `check-in-${id}`;

/**
 * Where a tile is in its confirm beat.
 *
 * `leaving` exists only so the CTA can fade rather than vanish between frames — it renders exactly as
 * `confirmed` does, at zero opacity. `done` is terminal for the life of the feed: the CTA is gone and
 * must not come back while the projection catches up.
 */
export type CheckInPhase = 'idle' | 'confirmed' | 'leaving' | 'done';

export interface CheckInFeedback {
  /** The confirm beat this tile is in. `idle` for a tile that has not been checked in here. */
  phaseFor: (id: string) => CheckInPhase;
  /** Start this tile's confirm beat: hold the label, fade, then stop offering the CTA. */
  confirm: (id: string) => void;
  /** Say the check-in did not happen, for this tile. */
  fail: (id: string) => void;
}

const drop = (record: Record<string, CheckInPhase>, id: string): Record<string, CheckInPhase> => {
  const next = { ...record };
  delete next[id];

  return next;
};

/**
 * The outcome of a check-in, for every tile in the feed — success as a beat on the button itself,
 * failure as a toast.
 *
 * ONE instance, owned by the feed, because that is where both mutations settle: the event flow and the
 * resource-booking flow report through the same two calls rather than each growing its own copy of the
 * timers and its own idea of what a failure says.
 *
 * The confirm beat is keyed by item id, not single-valued: two tiles can legitimately be mid-beat at
 * the same time, and a second tile's success must not cancel or inherit the first tile's. Failures need
 * no state at all — `toastId` gives one notice per tile, replaced rather than stacked when the same
 * tile fails again, and `autoClose` closes it. That is the whole of the requirement, so there is no
 * timer here to leak.
 *
 * The success beat deliberately does not wait for the read side. `useSettleAfterWrite` is already
 * re-reading behind this, but how long that takes is a projection question — the user gets their
 * confirmation on a fixed, local schedule either way.
 */
export const useCheckInFeedback = (): CheckInFeedback => {
  const intl = useIntl();
  const [phases, setPhases] = useState<Record<string, CheckInPhase>>({});
  // One timer per tile, so nothing here is shared between tiles and all of it is cancellable on
  // unmount — a feed left mid-beat must not set state on a screen the user has gone from.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const pending = timers.current;

    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const hold = useCallback((id: string, ms: number, then: () => void): void => {
    const running = timers.current.get(id);
    if (running) clearTimeout(running);

    timers.current.set(
      id,
      setTimeout(() => {
        timers.current.delete(id);
        then();
      }, ms),
    );
  }, []);

  const confirm = useCallback(
    (id: string): void => {
      setPhases((current) => ({ ...current, [id]: 'confirmed' }));
      hold(id, CHECK_IN_CONFIRM_MS, () => {
        setPhases((current) => ({ ...current, [id]: 'leaving' }));
        hold(id, CHECK_IN_FADE_MS, () => setPhases((current) => ({ ...current, [id]: 'done' })));
      });
    },
    [hold],
  );

  const fail = useCallback(
    (id: string): void => {
      // A previous attempt on this tile may have left it mid-beat (it cannot have finished one — the
      // CTA is gone by then), so stand that down rather than confirming something that just failed.
      const running = timers.current.get(id);
      if (running) {
        clearTimeout(running);
        timers.current.delete(id);
      }
      setPhases((current) => drop(current, id));

      // No styling passed on purpose. A toast looks the same everywhere at HqO — admin and the tenant
      // portal included — and that consistency is the point of taking it from the shared package
      // rather than dressing it per surface.
      toast.error(intl.formatMessage({ id: 'activity.checkIn.error' }), {
        autoClose: CHECK_IN_ERROR_MS,
        toastId: checkInToastId(id),
      });
    },
    [intl],
  );

  const phaseFor = useCallback((id: string): CheckInPhase => phases[id] ?? 'idle', [phases]);

  return { phaseFor, confirm, fail };
};
